<template>
  <div class="claim-stage">
    <div class="claim-stage-indicator">
      <span class="claim-stage-track" aria-hidden="true">
        <template v-for="stepNumber in total">
          <span
            v-if="stepNumber > 1"
            :key="`bar-${stepNumber}`"
            class="claim-stage-bar"
            :class="barClass(stepNumber)"
          />
          <span :key="`dot-${stepNumber}`" class="claim-stage-dot" :class="dotClass(stepNumber)" />
        </template>
      </span>
      <p class="claim-stage-label">
        Step {{ step }}<span class="sr-only"> of {{ total }}</span
        >: {{ label }}
      </p>
    </div>
    <img src="@/assets/illustrations/auth/agentgrid-panel.jpg" class="claim-stage-art" alt="" />
  </div>
</template>

<script>
/**
 * Agent Grid's claim flow renders its steps on a ruled stage beside the form; signing in is the
 * first of them, so the auth views borrow the stage rather than the illustration.
 *
 * The dots are decorative — the caption carries the whole position.
 */
export default {
  name: 'AgentGridClaimStage',
  props: {
    label: {
      type: String,
      default: 'Sign in'
    },
    step: {
      type: Number,
      default: 1
    },
    total: {
      type: Number,
      default: 3
    }
  },
  methods: {
    dotClass(stepNumber) {
      if (stepNumber === this.step) {
        // The step in progress is a square; the rest are dots.
        return 'current';
      }
      return stepNumber < this.step ? 'done' : 'upcoming';
    },
    barClass(stepNumber) {
      if (stepNumber <= this.step) {
        return 'done';
      }
      return stepNumber === this.step + 1 ? 'next' : 'upcoming';
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_auth-agentgrid-tokens.scss';

.claim-stage {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 40px 24px;
  background-color: var(--agentgrid-background-default);
  // Ruled paper under a wash that deepens toward the bottom. One `background-image` carries all
  // three layers, so the grid keeps its own tile size.
  background-image: linear-gradient(to right, var(--agentgrid-grid-line) 1px, transparent 1px),
    linear-gradient(to bottom, var(--agentgrid-grid-line) 1px, transparent 1px),
    linear-gradient(to bottom, transparent, var(--agentgrid-accent-wash));
  background-size: 16px 16px, 16px 16px, 100% 100%;
}

.claim-stage-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

// The claim flow centres the artifact being claimed here; this sign-in has no artifact yet, so the
// brand panel stands in for it.
.claim-stage-art {
  max-width: 420px;
  width: 100%;
  margin: auto 0;
  border-radius: $agentgrid-radius-panel;
  box-shadow: 0 1px 2px rgba(36, 36, 36, 0.08);
}

.claim-stage-track {
  display: flex;
  align-items: center;
}

.claim-stage-dot {
  width: 10px;
  height: 10px;

  &.current {
    background: var(--agentgrid-accent);
  }

  &.done {
    border-radius: 50%;
    background: var(--agentgrid-accent);
  }

  &.upcoming {
    border-radius: 50%;
    background: var(--agentgrid-text-primary);
  }
}

.claim-stage-bar {
  width: 32px;
  height: 3px;

  &.done {
    background: var(--agentgrid-accent);
  }

  &.next {
    background: linear-gradient(to right, var(--agentgrid-accent), var(--agentgrid-text-primary));
  }

  &.upcoming {
    background: var(--agentgrid-text-primary);
  }
}

.claim-stage-label {
  font-family: $agentgrid-title-font;
  font-size: $agentgrid-label-size;
  line-height: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--agentgrid-text-secondary);
  margin: 0;
}
</style>
